/**
 * @fileoverview WelcomeScenes — cenas autorais (Direção A: "Constelação")
 * pro carrossel da WelcomeScreen. Uma cena por slide, na linguagem do símbolo
 * da marca (curva de swipe + triângulo de play).
 *
 * Substitui o antigo `.artGlyph` (blob de CSS placeholder). Concepção aprovada
 * pelo dono: constelação SVG — nós (estrelas) ligados por linhas que traçam a
 * ideia de cada slide, com a curva de swipe costurando a identidade Swipe TV.
 *
 * ===== Legibilidade a 3m é requisito, não acabamento =====
 * Linha fina + opacidade baixa some numa TV a 3 metros. A régua desta folha
 * (verificada por `WelcomeScenes.contrast.test.ts`): elemento gráfico que
 * PRECISA ser visto tem contraste >= 3:1 contra o --bg-base (#0a0a1a), que é o
 * mínimo da WCAG pra elemento não-textual. Acento decorativo pode ficar abaixo,
 * mas aí ele não pode carregar informação nenhuma.
 *
 * ===== Regras de compat/render (piso Chr38, webOS/Tizen 2017+) =====
 * - SVG INLINE (mesmo padrão de PlayerIcons.tsx: render consistente entre
 *   Tizen/webOS/web; emoji varia por TV e vira tofu no piso).
 * - Animação só via CSS `@keyframes` de `transform`/`opacity` (composite-only,
 *   sem reflow/paint). SEM `filter`/blur DENTRO do SVG (não existe no piso e é
 *   caro onde existe). O "glow" é um radialGradient de borda dura (cor ->
 *   transparente), zero blur.
 * - Cores LITERAIS ASSADAS nos <stop>/<fill> (os hex do tokens.css copiados à
 *   mão): #7c3aed (--app-primary), #6d28d9 (--app-secondary), #ec4899
 *   (--app-magenta), #a78bfa (fim do --app-gradient-soft). NUNCA `var()` — o
 *   `.ipk` remove as defs de custom property e `var()` viria VAZIO no runtime
 *   (ver docs de memória: ipk_flatten_remove_custom_props). Como o valor é
 *   literal no JSX, ele viaja assado no bundle (provado por grep no artefato).
 * - Identidade do APP (pré-provider): usa a paleta --app-*, NUNCA a marca do
 *   parceiro (--brand-*). Estas telas aparecem antes de qualquer login.
 * - As classes de animação (animA/animB/animC) e o glow são LIGADAS/DESLIGADAS
 *   por tier no WelcomeScreen.module.css (mid = 1 camada; high/ultra = 2-3 +
 *   glow; low/vlow/reduce-motion = estático). Aqui a cena é sempre renderizada
 *   INTEIRA e VISÍVEL sem animação — os @keyframes só oscilam a partir do
 *   estado-base, então "animação off" = cena estática coerente, nunca vazia.
 *
 * IDs de <defs> são prefixados por cena (sc1-/sc2-/sc3-) porque, embora só o
 * slide atual esteja montado (key={slide} no WelcomeScreen), IDs duplicados de
 * gradient num mesmo documento SVG resolvem pro primeiro e sujariam o render.
 */

import styles from './WelcomeScenes.module.css'

// Paleta ASSADA — cópia à mão dos hex de packages/ui/src/tokens.css. Ver o
// cabeçalho: `var()` não sobrevive ao `.ipk`, então o literal é obrigatório.
const PRIMARY = '#7c3aed' // --app-primary
const SECONDARY = '#6d28d9' // --app-secondary
const MAGENTA = '#ec4899' // --app-magenta
const LILAC = '#a78bfa' // fim do --app-gradient-soft
// QR real (QRDisplay.tsx) desenha módulos ESCUROS sobre fundo CLARO (dark
// #000000 / light #FFFFFF). A cena 3 faz um ECO ESTILIZADO disso (fio condutor
// visual com a tela seguinte de pareamento) — NÃO é cópia pixel-a-pixel: os
// módulos usam o quase-preto neutro do DS (--bg-deepest #0a0a0a), levemente mais
// suave que o #000 puro, e o card usa branco.
const QR_DARK = '#0a0a0a' // == --bg-deepest (quase-preto neutro do DS)
const QR_LIGHT = '#ffffff'
/**
 * Corpo escuro dos "objetos" das cenas (tela de device na 2, card de pôster na
 * 1). Neutro AUTORAL, de propósito FORA do gate de sincronismo: ele não é cor de
 * marca (--app-*), é o preenchimento que faz o objeto ler como objeto. Se um dia
 * virar token, ele entra no gate junto com os outros.
 */
const CARD_DEEP = '#171226'

interface SceneProps {
  /** Índice do slide (0,1,2). Fora do range renderiza a cena 1 (defensivo). */
  index: number
}

const SVG_PROPS = {
  viewBox: '0 0 320 320',
  width: '100%',
  height: '100%',
  role: 'presentation' as const,
  'aria-hidden': true,
  // preserveAspectRatio default (xMidYMid meet) centra e mantém proporção.
} as const

/**
 * Cena 1 — "Sua TV, do seu jeito": o ACERVO. O texto promete "tudo o que você
 * ama de assistir em um só lugar", então a cena mostra CONTEÚDO, não um botão.
 *
 * O que mudou e por quê (revisão de arte):
 *  - SAIU o triângulo de play gigante no centro. O `AppLogo` do topo da tela JÁ
 *    é um glifo de play da mesma marca — dois plays na primeira tela do produto
 *    era a marca competindo com ela mesma, e "play" ilustra o VERBO assistir,
 *    não o SUBSTANTIVO acervo. O play segue existindo nas cenas 2 e 3, pequeno e
 *    dentro de um device/módulo (aí ele é conteúdo tocando, não logo).
 *  - ENTROU um trilho em arco de 5 pôsteres (o mesmo objeto que o usuário vai
 *    ver na home do app), convergindo pro título central e costurado pela curva
 *    de swipe. Os pôsteres SÃO os nós da constelação: as linhas ligam o acervo
 *    ao centro = "tudo em um só lugar".
 *  - Sem UMA LETRA dentro do SVG (risco de i18n zero, como o resto das cenas):
 *    o que diz "pôster" é a PROPORÇÃO 2:3 + a barrinha de título embaixo, que
 *    lê a 3m como card, não como retângulo colorido.
 *
 * Legibilidade a 3m (medida): as linhas da constelação eram
 * `strokeWidth 1.4 / strokeOpacity 0.3` = contraste 1,65:1 sobre o --bg-base
 * (#0a0a1a) — abaixo do 3:1 que a WCAG pede pra elemento gráfico, ou seja,
 * ruído sub-limiar. Agora `2.6 / 0.65` = 3,62:1 (o 0,55 do pedido dá 2,90:1,
 * ainda abaixo de 3:1 — por isso 0,65). Fórmula: composição do lilás sobre o
 * fundo e razão de contraste WCAG; o cálculo reproduz o 1,65:1 do laudo.
 */
function Scene1() {
  return (
    <svg {...SVG_PROPS}>
      <defs>
        <radialGradient id="sc1-halo" cx="50%" cy="52%" r="52%">
          <stop offset="0%" stopColor={PRIMARY} stopOpacity="0.55" />
          <stop offset="55%" stopColor={PRIMARY} stopOpacity="0.14" />
          <stop offset="100%" stopColor={PRIMARY} stopOpacity="0" />
        </radialGradient>
        {/* Pôster em destaque (o "título da vez"): gradiente da marca. */}
        <linearGradient id="sc1-hero" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={PRIMARY} />
          <stop offset="100%" stopColor={MAGENTA} />
        </linearGradient>
        {/* Vizinhos: corpo escuro do card + moldura em gradiente (mesma família
            visual dos devices da cena 2). */}
        <linearGradient id="sc1-frame" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={PRIMARY} />
          <stop offset="100%" stopColor={MAGENTA} />
        </linearGradient>
        <linearGradient id="sc1-frame2" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor={SECONDARY} />
          <stop offset="100%" stopColor={LILAC} />
        </linearGradient>
        <linearGradient id="sc1-swipe" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={LILAC} stopOpacity="0" />
          <stop offset="45%" stopColor={LILAC} stopOpacity="0.9" />
          <stop offset="100%" stopColor={MAGENTA} />
        </linearGradient>
      </defs>

      {/* Glow (radialGradient de borda dura, sem blur) — pulsa no high/ultra. */}
      <circle cx="160" cy="166" r="140" fill="url(#sc1-halo)" className={`${styles.glow} ${styles.glowPulse} ${styles.animB}`} />

      {/* Constelação: linhas ligando as estrelas do alto ao acervo do centro
          ("tudo num lugar"). Convergem no TOPO do pôster em destaque (160,104),
          não no meio — o miolo é do conteúdo. */}
      <g stroke={LILAC} strokeWidth="2.6" strokeOpacity="0.65" fill="none" strokeLinecap="round">
        <line x1="160" y1="104" x2="58" y2="52" />
        <line x1="160" y1="104" x2="262" y2="46" />
        <line x1="160" y1="104" x2="30" y2="126" />
        <line x1="160" y1="104" x2="292" y2="122" />
        <line x1="160" y1="104" x2="118" y2="26" />
        <line x1="160" y1="104" x2="214" y2="22" />
      </g>

      {/* Curva de swipe (o "S" da marca) — o gesto que percorre o acervo, POR
          BAIXO do trilho.
          Ela passava ATRÁS dos cards: o miolo ficava tapado e só sobravam dois
          tocos nas laterais, que a 3m leem como sujeira, não como curva (visto em
          screenshot @1920). Um traço que só aparece pela metade é pior que traço
          nenhum. Aqui ela corre inteira abaixo do card mais baixo (y≈253 depois
          da rotação), dentro do quadro. */}
      <path
        d="M22 262 C 92 296, 228 296, 298 258"
        fill="none"
        stroke="url(#sc1-swipe)"
        strokeWidth="6"
        strokeLinecap="round"
        opacity="0.75"
      />

      {/* Nós da constelação (estrelas) — twinkle (animA, camada essencial). */}
      <g className={`${styles.twinkle} ${styles.animA}`} fill={LILAC}>
        <circle cx="58" cy="52" r="4.4" opacity="0.95" />
        <circle cx="262" cy="46" r="3.8" fill="#ffffff" opacity="0.9" />
        <circle cx="30" cy="126" r="3.4" opacity="0.85" />
        <circle cx="292" cy="122" r="4.4" fill={MAGENTA} opacity="0.9" />
        <circle cx="118" cy="26" r="3.2" opacity="0.8" />
        <circle cx="214" cy="22" r="3.6" fill="#ffffff" opacity="0.85" />
      </g>

      {/* ===== Trilho em arco de pôsteres (o acervo) =====
          Proporção 2:3 (pôster de verdade), tamanho e altura caindo pras
          pontas = profundidade sem blur nenhum. Ordem de pintura: de fora pra
          dentro, então o destaque fica por cima. A barrinha embaixo de cada card
          é o lugar do título (sem texto — i18n zero). */}
      <g transform="rotate(-13 62 214)">
        <rect x="34" y="164" width="56" height="84" rx="7" fill={CARD_DEEP} stroke="url(#sc1-frame2)" strokeWidth="3" opacity="0.85" />
        <rect x="42" y="234" width="40" height="5" rx="2.5" fill={LILAC} opacity="0.65" />
      </g>
      <g transform="rotate(13 258 214)">
        <rect x="230" y="164" width="56" height="84" rx="7" fill={CARD_DEEP} stroke="url(#sc1-frame2)" strokeWidth="3" opacity="0.85" />
        <rect x="238" y="234" width="40" height="5" rx="2.5" fill={LILAC} opacity="0.65" />
      </g>
      <g transform="rotate(-7 104 196)">
        <rect x="72" y="140" width="64" height="96" rx="8" fill={CARD_DEEP} stroke="url(#sc1-frame)" strokeWidth="3.5" />
        <rect x="81" y="220" width="46" height="6" rx="3" fill={LILAC} opacity="0.7" />
      </g>
      <g transform="rotate(7 216 196)">
        <rect x="184" y="140" width="64" height="96" rx="8" fill={CARD_DEEP} stroke="url(#sc1-frame)" strokeWidth="3.5" />
        <rect x="193" y="220" width="46" height="6" rx="3" fill={LILAC} opacity="0.7" />
      </g>
      {/* Pôster em destaque (centro, à frente): o único com fill de marca. */}
      <g>
        <rect x="124" y="104" width="72" height="108" rx="9" fill="url(#sc1-hero)" />
        <rect x="124" y="104" width="72" height="108" rx="9" fill="none" stroke="#ffffff" strokeWidth="2" strokeOpacity="0.28" />
        <rect x="134" y="194" width="52" height="7" rx="3.5" fill="#ffffff" opacity="0.75" />
      </g>

      {/* Estrela cadente — streak diagonal (animC, camada premium). Repouso
          fora do quadro (transform attribute) → invisível quando estático. */}
      <g className={`${styles.streak} ${styles.animC}`} transform="translate(-80,-60)">
        <line x1="-24" y1="-16" x2="6" y2="0" stroke={LILAC} strokeWidth="2.5" strokeLinecap="round" strokeOpacity="0.85" />
        <circle cx="8" cy="1" r="3" fill="#ffffff" />
      </g>
    </svg>
  )
}

/**
 * Cena 2 — "Sua experiência te segue": continuidade cross-device. Três telas
 * (TV grande, tablet, celular) como nós da constelação, ligadas por uma curva
 * de swipe. Um token de play viaja pela curva de um device pro outro (a sessão
 * que te acompanha). Cada tela tem o mesmo play dentro (mesmo conteúdo).
 */
function Scene2() {
  return (
    <svg {...SVG_PROPS}>
      <defs>
        <radialGradient id="sc2-halo" cx="50%" cy="45%" r="55%">
          <stop offset="0%" stopColor={PRIMARY} stopOpacity="0.4" />
          <stop offset="60%" stopColor={PRIMARY} stopOpacity="0.1" />
          <stop offset="100%" stopColor={PRIMARY} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="sc2-frame" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={PRIMARY} />
          <stop offset="100%" stopColor={MAGENTA} />
        </linearGradient>
        <linearGradient id="sc2-thread" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={LILAC} />
          <stop offset="100%" stopColor={MAGENTA} />
        </linearGradient>
      </defs>

      <circle cx="160" cy="150" r="150" fill="url(#sc2-halo)" className={`${styles.glow} ${styles.glowPulse} ${styles.animC}`} />

      {/* Curva de swipe costurando os 3 devices (TV -> tablet -> celular).
          Tracejado era `2 9` com stroke 3.5: a 3m cada traço media ~3px de
          comprimento por 1,9 de espessura, com 8,7px de buraco entre eles — a
          linha sumia e sobrava uma poeira. Agora `6 10` com stroke 4 (traço 2x
          mais longo e mais grosso) e opacidade 0.8 = 3,90:1 sobre o --bg-base,
          acima do 3:1 da WCAG pra elemento gráfico. */}
      <path
        d="M150 108 C 78 128, 70 208, 108 220 C 150 232, 214 214, 248 196"
        fill="none"
        stroke="url(#sc2-thread)"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray="6 10"
        opacity="0.8"
      />

      {/* Constelação de fundo (twinkle animC junto do glow). */}
      <g className={`${styles.twinkle} ${styles.animC}`} fill={LILAC}>
        <circle cx="46" cy="70" r="2.6" opacity="0.7" />
        <circle cx="286" cy="86" r="3" fill="#ffffff" opacity="0.8" />
        <circle cx="292" cy="240" r="2.4" opacity="0.6" />
        <circle cx="40" cy="250" r="2.8" opacity="0.6" />
      </g>

      {/* TV grande (centro) — o device principal. */}
      <g>
        <rect x="96" y="70" width="128" height="82" rx="9" fill={CARD_DEEP} stroke="url(#sc2-frame)" strokeWidth="3.5" />
        <polygon points="150,96 150,126 176,111" fill={LILAC} />
        <rect x="140" y="152" width="40" height="6" rx="3" fill={PRIMARY} />
      </g>

      {/* Tablet (inferior esquerda). */}
      <g>
        <rect x="58" y="196" width="66" height="50" rx="7" fill={CARD_DEEP} stroke="url(#sc2-frame)" strokeWidth="3" />
        <polygon points="82,210 82,232 100,221" fill={LILAC} opacity="0.92" />
      </g>

      {/* Celular (direita). */}
      <g>
        <rect x="234" y="168" width="40" height="66" rx="8" fill={CARD_DEEP} stroke="url(#sc2-frame)" strokeWidth="3" />
        <polygon points="248,188 248,208 264,198" fill={LILAC} opacity="0.92" />
      </g>

      {/* Devices "acendem" em sequência (animB, camada premium) — halos.
          Cada rect leva o gate .animB (pra o tier desligar) + a classe de
          fase (seq1/2/3 = mesmo keyframe, delays escalonados). */}
      <g fill="none" stroke={MAGENTA} strokeWidth="2.5">
        <rect className={`${styles.seq1} ${styles.animB}`} x="96" y="70" width="128" height="82" rx="9" strokeOpacity="0.9" />
        <rect className={`${styles.seq2} ${styles.animB}`} x="58" y="196" width="66" height="50" rx="7" strokeOpacity="0.9" />
        <rect className={`${styles.seq3} ${styles.animB}`} x="234" y="168" width="40" height="66" rx="8" strokeOpacity="0.9" />
      </g>

      {/* Token de play viajando pela curva (animA, camada essencial). O
          `transform` ATTRIBUTE fixa o repouso (funciona no Chr38): quando a
          animação está desligada (mid não roda animA? roda; low/vlow/reduce
          desligam) o token descansa sobre a curva, nunca no canto (0,0). O
          keyframe usa translates ABSOLUTOS e sobrepõe o atributo enquanto roda.

          REPOUSO em (88,167): é o ponto t=0,5 da PRIMEIRA curva de Bézier do
          fio (entre a TV e o tablet), calculado, não chutado. Antes descansava
          em (128,196), encostado no canto de cima do tablet — e sem animação
          (TODO Chr38, mais low/vlow em qualquer motor) aquilo lia como BADGE DE
          NOTIFICAÇÃO grudado no aparelho, que é o oposto da ideia. Sobre a curva
          e longe dos dois retângulos, ele lê como "a sessão em trânsito" mesmo
          parado. */}
      <g className={`${styles.travel} ${styles.animA}`} transform="translate(88,167)">
        <circle cx="0" cy="0" r="11" fill="url(#sc2-frame)" />
        <polygon points="-3.5,-5 -3.5,5 5,0" fill="#ffffff" />
      </g>
    </svg>
  )
}

/**
 * Cena 3 — "Entrar é fácil": QR/pareamento. Um celular aponta a câmera pra um
 * cartão QR BRANCO com UM padrão de detecção (finder pattern) escuro e módulos
 * soltos: um ECO ESTILIZADO do QR real de QRDisplay.tsx (fio condutor com a
 * próxima tela) e, de propósito, INESCANEÁVEL — ver a nota de "QR-idade" no
 * corpo da cena. Módulos escuros formam um triângulo de play. Linha de scan
 * varre.
 */
function Scene3() {
  return (
    <svg {...SVG_PROPS}>
      <defs>
        <radialGradient id="sc3-halo" cx="62%" cy="45%" r="55%">
          <stop offset="0%" stopColor={PRIMARY} stopOpacity="0.42" />
          <stop offset="60%" stopColor={PRIMARY} stopOpacity="0.1" />
          <stop offset="100%" stopColor={PRIMARY} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="sc3-frame" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={PRIMARY} />
          <stop offset="100%" stopColor={MAGENTA} />
        </linearGradient>
        <linearGradient id="sc3-beam" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={LILAC} stopOpacity="0.5" />
          <stop offset="100%" stopColor={LILAC} stopOpacity="0" />
        </linearGradient>
        <linearGradient id="sc3-scan" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={MAGENTA} stopOpacity="0" />
          <stop offset="50%" stopColor={MAGENTA} />
          <stop offset="100%" stopColor={MAGENTA} stopOpacity="0" />
        </linearGradient>
      </defs>

      <circle cx="196" cy="150" r="150" fill="url(#sc3-halo)" className={`${styles.glow} ${styles.animB}`} />

      {/* Constelação de fundo. */}
      <g className={styles.animC} fill={LILAC}>
        <circle cx="44" cy="66" r="2.6" opacity="0.7" />
        <circle cx="292" cy="72" r="3" fill="#ffffff" opacity="0.8" />
        <circle cx="300" cy="228" r="2.4" opacity="0.6" />
        <circle cx="150" cy="278" r="2.6" opacity="0.6" />
      </g>

      {/* Feixe da câmera do celular pro cartão (estático, sutil). */}
      <polygon points="150,150 176,120 176,196" fill="url(#sc3-beam)" opacity="0.8" />

      {/* Celular (esquerda) apontando pro QR — moldura em gradient, visor com
          cantos de mira (a "câmera"). */}
      <g>
        <rect x="60" y="92" width="92" height="150" rx="16" fill={CARD_DEEP} stroke="url(#sc3-frame)" strokeWidth="4" />
        <rect x="72" y="112" width="68" height="110" rx="8" fill="#0c0918" />
        {/* Cantos de mira do viewfinder. */}
        <g stroke={LILAC} strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.9">
          <path d="M84 128 v10 M84 128 h10" />
          <path d="M128 128 h-10 M128 128 v10" />
          <path d="M84 206 v-10 M84 206 h10" />
          <path d="M128 206 v-10 M128 206 h-10" />
        </g>
        {/* Notch/câmera. */}
        <circle cx="106" cy="102" r="2.4" fill={SECONDARY} />
      </g>

      {/* Cartão QR BRANCO — eco estilizado do QR real (dark ~preto / light #fff).

          "QR-IDADE" DELIBERADAMENTE BAIXA (risco de leitura levantado na
          revisão): esta é a ÚNICA imagem de QR desta tela, e a tela seguinte tem
          um QR DE VERDADE pra apontar a câmera. Um usuário mais velho pode tentar
          escanear a ilustração e ficar sem resposta logo na 1ª tela do app.
          Por isso: UM finder pattern (o QR real tem TRÊS — com um só, nenhuma
          câmera do mundo trava nele) e módulos de canto mais mole (rx 2). Pra
          pessoa continua "aquilo é um QR code" (o finder pattern é o ícone
          universal), que é o fio condutor com o pareamento; pra câmera é
          inescaneável por construção. */}
      <g>
        <rect x="182" y="98" width="118" height="118" rx="12" fill={QR_LIGHT} />
        {/* Finder pattern (padrão de detecção): dark 7u / white 5u / dark 3u. */}
        <FinderPattern x={196} y={112} />

        {/* Módulos soltos formando um triângulo de play (canto livre) — o play
            da marca "escondido" nos módulos do QR. */}
        <g fill={QR_DARK}>
          <rect x="254" y="170" width="8" height="8" rx="2" />
          <rect x="254" y="182" width="8" height="8" rx="2" />
          <rect x="254" y="194" width="8" height="8" rx="2" />
          <rect x="266" y="176" width="8" height="8" rx="2" />
          <rect x="266" y="188" width="8" height="8" rx="2" />
          <rect x="278" y="182" width="8" height="8" rx="2" />
          {/* Textura solta (ex-finders): diz "código" sem formar padrão de
              detecção nenhum. */}
          <rect x="252" y="112" width="9" height="9" rx="2" opacity="0.9" />
          <rect x="266" y="112" width="9" height="9" rx="2" opacity="0.75" />
          <rect x="252" y="126" width="9" height="9" rx="2" opacity="0.75" />
          <rect x="280" y="126" width="9" height="9" rx="2" opacity="0.6" />
          <rect x="196" y="170" width="9" height="9" rx="2" opacity="0.9" />
          <rect x="210" y="184" width="9" height="9" rx="2" opacity="0.75" />
          <rect x="196" y="198" width="9" height="9" rx="2" opacity="0.6" />
          <rect x="224" y="198" width="9" height="9" rx="2" opacity="0.6" />
          <rect x="238" y="142" width="7" height="7" rx="2" opacity="0.7" />
        </g>

        {/* Linha de scan varrendo o cartão (animA, camada essencial). Base
            opacity 0 (via .scanSweep) → invisível quando estático; o keyframe
            faz o fade-in + translateY dentro da área do cartão (y 110..206). */}
        <rect className={`${styles.scanSweep} ${styles.animA}`} x="188" y="110" width="106" height="4" rx="2" fill="url(#sc3-scan)" />
      </g>
    </svg>
  )
}

/** Finder pattern de QR: 7 módulos escuros / 5 claros / 3 escuros, concêntricos.
 *  Unidade ~5px (7u = 35px). Cantos quase retos (rx=2) pra ler como QR.
 *  Usado UMA vez na cena 3 (o QR de verdade tem três) — ver a nota de QR-idade. */
function FinderPattern({ x, y }: { x: number; y: number }) {
  return (
    <g>
      <rect x={x} y={y} width={35} height={35} rx={3} fill={QR_DARK} />
      <rect x={x + 5} y={y + 5} width={25} height={25} rx={2} fill={QR_LIGHT} />
      <rect x={x + 10} y={y + 10} width={15} height={15} rx={2} fill={QR_DARK} />
    </g>
  )
}

/** Ponto de entrada — despacha pra cena do slide (0,1,2). */
export function WelcomeScene({ index }: SceneProps) {
  if (index === 1) return <Scene2 />
  if (index === 2) return <Scene3 />
  return <Scene1 />
}
